import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";

const FileDropzone = ({
  onFilesSelected,
  acceptedFileTypes = [".pdf"],
  maxFileSize = 10 * 1024 * 1024, // 10MB
  maxFiles = 5,
  multiple = true,
  disabled = false,
  existingFiles = [],
  onFileRemove,
  generateFileName,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);

  const validateFile = (file) => {
    const errors = [];

    // Check file type
    if (acceptedFileTypes.length > 0) {
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      if (!acceptedFileTypes.includes(fileExtension)) {
        errors.push(
          `Invalid file type. Accepted types: ${acceptedFileTypes.join(", ")}`
        );
      }
    }

    // Check file size
    if (file.size > maxFileSize) {
      errors.push(
        `File too large. Maximum size: ${(maxFileSize / 1024 / 1024).toFixed(1)}MB`
      );
    }

    return errors;
  };

  const processFiles = useCallback(
    (files) => {
      const fileArray = Array.from(files);
      const validFiles = [];
      const fileErrors = [];

      // Check total file count
      if (existingFiles.length + fileArray.length > maxFiles) {
        fileErrors.push(`Too many files. Maximum allowed: ${maxFiles}`);
        setErrors(fileErrors);
        return;
      }

      fileArray.forEach((file) => {
        const validationErrors = validateFile(file);
        if (validationErrors.length > 0) {
          fileErrors.push(`${file.name}: ${validationErrors.join(", ")}`);
        } else {
          // Generate file ID and metadata
          const fileData = {
            id: generateFileName
              ? generateFileName()
              : `FILE_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            name: file.name,
            type: file.type,
            size: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
            file,
            uploadedAt: new Date().toISOString(),
            status: "ready", // ready, uploading, uploaded, error
          };
          validFiles.push(fileData);
        }
      });

      setErrors(fileErrors);

      if (validFiles.length > 0) {
        onFilesSelected(validFiles);

        // Simulate upload progress for demo
        validFiles.forEach((fileData) => {
          simulateUpload(fileData.id);
        });
      }
    },
    [
      existingFiles.length,
      maxFiles,
      onFilesSelected,
      generateFileName,
      maxFileSize,
      acceptedFileTypes,
    ]
  );

  const simulateUpload = (fileId) => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress((prev) => ({ ...prev, [fileId]: progress }));

      if (progress >= 100) {
        clearInterval(interval);
        setTimeout(() => {
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[fileId];
            return newProgress;
          });
        }, 1000);
      }
    }, 100);
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        processFiles(files);
      }
    },
    [disabled, processFiles]
  );

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setDragActive(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  const getFileIcon = (filename) => {
    const extension = filename.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return <InsertDriveFile sx={{ color: "#d32f2f" }} />;
      default:
        return <InsertDriveFile />;
    }
  };

  return (
    <Box>
      {/* Dropzone */}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        sx={{
          border: 2,
          borderStyle: "dashed",
          borderColor: dragActive
            ? "primary.main"
            : disabled
              ? "grey.300"
              : "grey.400",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
          bgcolor: dragActive
            ? "action.hover"
            : disabled
              ? "grey.50"
              : "background.paper",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": disabled
            ? {}
            : {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
        }}
        onClick={() =>
          !disabled && document.getElementById("file-upload-input").click()
        }
      >
        <CloudUpload
          sx={{
            fontSize: 48,
            color: disabled
              ? "grey.400"
              : dragActive
                ? "primary.main"
                : "grey.500",
            mb: 2,
          }}
        />
        <Typography
          variant="h6"
          gutterBottom
          color={disabled ? "text.disabled" : "text.primary"}
        >
          {dragActive ? "Drop files here" : "Click to upload or drag and drop"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {acceptedFileTypes.join(", ").toUpperCase()} files up to{" "}
          {formatFileSize(maxFileSize)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Maximum {maxFiles} files ({existingFiles.length}/{maxFiles} used)
        </Typography>

        <input
          id="file-upload-input"
          type="file"
          multiple={multiple}
          accept={acceptedFileTypes.join(",")}
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={disabled}
        />
      </Box>

      {/* Error Messages */}
      {errors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Errors:
          </Typography>
          {errors.map((error, index) => (
            <Typography key={index} variant="body2">
              • {error}
            </Typography>
          ))}
        </Alert>
      )}

      {/* File List */}
      {existingFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Uploaded Files ({existingFiles.length})
          </Typography>
          <List>
            {existingFiles.map((file) => (
              <ListItem key={file.id} divider>
                <ListItemIcon>{getFileIcon(file.name)}</ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {file.name}
                      {file.status === "uploaded" && (
                        <CheckCircle color="success" fontSize="small" />
                      )}
                      {file.status === "error" && (
                        <ErrorIcon color="error" fontSize="small" />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {file.size} • ID: {file.id}
                      </Typography>
                      {uploadProgress[file.id] && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={uploadProgress[file.id]}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            Uploading: {uploadProgress[file.id]}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
                {onFileRemove && !uploadProgress[file.id] && (
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => onFileRemove(file.id)}
                      disabled={disabled}
                    >
                      <Delete />
                    </IconButton>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default FileDropzone;
